import { Component, Input, EventEmitter, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LawFirm, LawFirmEmployeeType } from '@models/lawFirms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { LawFirmSearchState } from '@state/algolia-search/law-firms-search';
import { LawFirmSearchDispatch } from '@state/algolia-search/law-firms-search/dispatch';
import {
	LawyerSearchModels,
	LawyerSearchState,
} from '@state/algolia-search/lawyer-search';
import { LawyerSearchDispatch } from '@state/algolia-search/lawyer-search/dispatch';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import {
	CreateLawFirmComponent,
	CreateOrUpdateLawFirmModel,
} from '../create/create.component';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TestHooks } from './search.hooks';

export enum DropdownSelect {
	SELECTLAWFIRM = 'SelectLawFirm',
	SELECTLAWYER = 'SelectLawyer',
	SELECTCLERK = 'SelectClerk',
}

@UntilDestroy()
@Component({
	selector: 'app-law-firm-search',
	templateUrl: './search.component.html',
	standalone: false,
})
export class LawFirmSearchComponent {
	testHooks = TestHooks;
	public showLawFirmsSearchResult = false;
	public loading = false;
	public ICON = ICON;
	public lawFirmName = '';
	public matterId;
	public dropdownSelect = DropdownSelect;
	public lawyers = [];
	public clerks = [];
	@Input() label;
	@Input() placeholder;
	@Input() showClerk;

	@Output() onSelectedLawfirm = new EventEmitter<LawFirm>();
	@Output() onCreateLawfirm = new EventEmitter<LawFirm>();
	@Output() onSelectedLawyer = new EventEmitter<string>();
	@Output() onSelectedClerk = new EventEmitter<string>();

	@Select(SelectedMatter.id) matterId$: Observable<string>;
	@Select(LawFirmSearchState.getList) lawFirms$: Observable<LawFirm[]>;
	@Select(LawFirmSearchState.getSearching)
	searchingLawFirms$: Observable<boolean>;

	@Select(LawyerSearchState.getList) lawyers$: Observable<
		LawyerSearchModels.LayerSearchResult[]
	>;
	@Select(LawyerSearchState.getSearching)
	searchingLawyers$: Observable<boolean>;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	constructor(
		private dialog: MatDialog,
		private lawyerSearchDispatch: LawyerSearchDispatch,
		private lawfirmSearchDispatch: LawFirmSearchDispatch,
	) {
		this.matterId$
			.pipe(untilDestroyed(this))
			.subscribe((value) => (this.matterId = value));
		this.lawyers$.pipe(untilDestroyed(this)).subscribe((value) => {
			this.lawyers = value.filter((employee) => employee.role === 'lawyer');
			this.clerks = value.filter((employee) => employee.role === 'clerk');
		});
	}

	searchEmployees(employeeSearchTerm: string) {
		if (this.showClerk) {
			this.lawyerSearchDispatch.Search({ searchValue: employeeSearchTerm });
		} else {
			this.lawyerSearchDispatch.Search({
				searchValue: employeeSearchTerm,
				lawFirmId: '',
				role: LawFirmEmployeeType.LAWYER,
			});
		}
	}

	searchLawFirms(lawFirmSearchTerm) {
		this.showLawFirmsSearchResult = true;
		this.loading = false;
		this.lawfirmSearchDispatch.Search(lawFirmSearchTerm);
	}

	search = ($event) => {
		this.loading = true;
		this.searchLawFirms($event);
		// if (!this.isLeapMatter) {
		this.searchEmployees($event);
		// }
	};

	createLawFirm() {
		const lawFirmModel: CreateOrUpdateLawFirmModel = {
			name: this.lawFirmName,
			lawFirm: null,
		};
		const dialogRef = this.dialog.open(CreateLawFirmComponent, {
			width: '800px',
			height: '700px',
			data: lawFirmModel,
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.onCreateLawfirm.emit(result);
			}
		});
	}

	onSelect = (ev: DropdownResult<string | LawFirm>) => {
		if (ev.groupType === DropdownSelect.SELECTLAWFIRM) {
			this.onSelectedLawfirm.emit(ev.result as LawFirm);
		} else if (ev.groupType === DropdownSelect.SELECTLAWYER) {
			this.onSelectedLawyer.emit(ev.result as string);
		} else if (ev.groupType === DropdownSelect.SELECTCLERK) {
			this.onSelectedClerk.emit(ev.result as string);
		}
	};
}
